import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { cn } from "@/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 right-4 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:max-w-[420px] font-mono",
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start space-x-4 overflow-hidden rounded-xl border-2 p-6 pr-12 shadow-2xl transition-all duration-300 font-mono backdrop-blur-xl bg-opacity-95 data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-top-full transform-gpu",
  {
    variants: {
      variant: {
        default:
          "border-gray-300 dark:border-gray-700 bg-white/95 dark:bg-black/95 text-gray-900 dark:text-gray-100 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)] dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.4),0_10px_10px_-5px_rgba(0,0,0,0.2)]",
        destructive:
          "border-red-500/50 bg-gradient-to-br from-red-50/95 to-red-100/95 dark:from-red-950/95 dark:to-red-900/95 text-red-900 dark:text-red-100 shadow-[0_20px_25px_-5px_rgba(239,68,68,0.25),0_10px_10px_-5px_rgba(239,68,68,0.1)] dark:shadow-[0_20px_25px_-5px_rgba(239,68,68,0.4),0_10px_10px_-5px_rgba(239,68,68,0.2)]",
        success:
          "border-green-500/50 bg-gradient-to-br from-green-50/95 to-green-100/95 dark:from-green-950/95 dark:to-green-900/95 text-green-900 dark:text-green-100 shadow-[0_20px_25px_-5px_rgba(34,197,94,0.25),0_10px_10px_-5px_rgba(34,197,94,0.1)] dark:shadow-[0_20px_25px_-5px_rgba(34,197,94,0.4),0_10px_10px_-5px_rgba(34,197,94,0.2)]",
        warning:
          "border-yellow-500/50 bg-gradient-to-br from-yellow-50/95 to-yellow-100/95 dark:from-yellow-950/95 dark:to-yellow-900/95 text-yellow-900 dark:text-yellow-100 shadow-[0_20px_25px_-5px_rgba(234,179,8,0.25),0_10px_10px_-5px_rgba(234,179,8,0.1)] dark:shadow-[0_20px_25px_-5px_rgba(234,179,8,0.4),0_10px_10px_-5px_rgba(234,179,8,0.2)]",
        info: "border-blue-500/50 bg-gradient-to-br from-blue-50/95 to-blue-100/95 dark:from-blue-950/95 dark:to-blue-900/95 text-blue-900 dark:text-blue-100 shadow-[0_20px_25px_-5px_rgba(59,130,246,0.25),0_10px_10px_-5px_rgba(59,130,246,0.1)] dark:shadow-[0_20px_25px_-5px_rgba(59,130,246,0.4),0_10px_10px_-5px_rgba(59,130,246,0.2)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const getToastIcon = (variant?: string) => {
  switch (variant) {
    case "success":
      return (
        <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
      );
    case "destructive":
      return (
        <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0" />
      );
    case "warning":
      return (
        <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
      );
    case "info":
      return (
        <Info className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
      );
    default:
      return null;
  }
};

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -50, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -50, scale: 0.95 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
    >
      <ToastPrimitives.Root
        ref={ref}
        className={cn(toastVariants({ variant }), className)}
        {...props}
      />
    </motion.div>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border bg-transparent px-3 text-sm font-bold font-mono ring-offset-background transition-all duration-200 hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive hover:scale-105 active:scale-95",
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-3 top-3 rounded-lg p-1.5 text-foreground/50 opacity-70 transition-all duration-200 hover:text-foreground hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/10 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600 hover:scale-110 active:scale-90",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      "text-sm font-bold font-mono tracking-wide leading-tight",
      className
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(
      "text-sm font-mono opacity-90 leading-relaxed mt-1",
      className
    )}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  getToastIcon,
};
